/**
 * AdminJournalAccesView
 * ─────────────────────
 * Tableau de bord admin — rubrique « Journal des accès ».
 *
 * Contrepartie de l'élargissement des habilitations : permet de répondre à
 * « qui a consulté ce dossier, et quand ? », en contrôle FSE+ comme en réponse
 * à un bénéficiaire exerçant son droit d'accès.
 *
 * Écran en LECTURE SEULE : aucune action ne modifie ni ne supprime une entrée
 * du journal. La purge éventuelle passe par un script exécuté manuellement.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck, RefreshCw, Filter, X, ChevronLeft, ChevronRight,
  FileSearch, AlertCircle, Clock
} from 'lucide-react';
import { journalAccesService } from '../../services/api';

const PAR_PAGE = 50;

const ROLES = {
  admin:          { label: 'Administrateur', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  referent:       { label: 'Référent',       cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  animateur:      { label: 'Intervenant',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  financeur:      { label: 'Financeur',      cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  'bénéficiaire': { label: 'Bénéficiaire',   cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const RoleBadge = ({ role }) => {
  const r = ROLES[role] || { label: role || '—', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${r.cls}`}>
      {r.label}
    </span>
  );
};

const fmtDateHeure = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const Tuile = ({ valeur, libelle }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
    <div className="text-2xl font-bold text-slate-900 tabular-nums"
         style={{ fontFamily: 'Nunito, sans-serif' }}>
      {valeur}
    </div>
    <div className="text-xs text-slate-500 mt-0.5">{libelle}</div>
  </div>
);

const FILTRES_VIDES = { role: '', type_acces: '', date_debut: '', date_fin: '' };

export default function AdminJournalAccesView() {
  const [entrees, setEntrees]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [typesAcces, setTypes]  = useState({});
  const [resume, setResume]     = useState(null);
  const [filtres, setFiltres]   = useState(FILTRES_VIDES);
  const [depuis, setDepuis]     = useState(0);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]     = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const params = { limite: PAR_PAGE, depuis };
      Object.entries(filtres).forEach(([k, v]) => { if (v) params[k] = v; });

      const [journal, stats] = await Promise.all([
        journalAccesService.list(params),
        journalAccesService.resume(30),
      ]);
      setEntrees(journal.data.entrees || []);
      setTotal(journal.data.total || 0);
      setTypes(journal.data.types_acces || {});
      setResume(stats.data);
    } catch (e) {
      setErreur(e.response?.data?.detail || "Impossible de charger le journal des accès.");
    } finally {
      setChargement(false);
    }
  }, [filtres, depuis]);

  useEffect(() => { charger(); }, [charger]);

  const modifierFiltre = (cle, valeur) => {
    setDepuis(0);
    setFiltres((f) => ({ ...f, [cle]: valeur }));
  };

  const filtresActifs = Object.values(filtres).some(Boolean);
  const page = Math.floor(depuis / PAR_PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAR_PAGE));

  const roleDominant = resume?.par_role
    ? Object.entries(resume.par_role).sort((a, b) => b[1] - a[1])[0]
    : null;

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-6 h-6 text-blue-600 flex-shrink-0" />
            <div>
              <CardTitle className="text-lg sm:text-xl font-bold"
                         style={{ fontFamily: 'Nunito, sans-serif' }}>
                Journal des accès aux dossiers
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Qui a consulté quel dossier, et quand. Lecture seule.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={charger}
                  disabled={chargement} className="rounded-xl">
            <RefreshCw className={`w-4 h-4 mr-2 ${chargement ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Volumétrie des 30 derniers jours */}
        {resume && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tuile valeur={resume.total} libelle="accès sur 30 jours" />
            <Tuile valeur={total} libelle="entrées correspondant au filtre" />
            <Tuile
              valeur={roleDominant ? (ROLES[roleDominant[0]]?.label || roleDominant[0]) : '—'}
              libelle="rôle le plus actif"
            />
            <Tuile
              valeur={Object.keys(resume.par_type || {}).length}
              libelle="types d'accès observés"
            />
          </div>
        )}

        {/* Filtres */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Filtrer</span>
            {filtresActifs && (
              <button
                onClick={() => { setFiltres(FILTRES_VIDES); setDepuis(0); }}
                className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
              >
                <X className="w-3 h-3" /> Tout effacer
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500">Rôle</span>
              <select
                value={filtres.role}
                onChange={(e) => modifierFiltre('role', e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Tous les rôles</option>
                {Object.entries(ROLES).map(([valeur, { label }]) => (
                  <option key={valeur} value={valeur}>{label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs text-slate-500">Type d'accès</span>
              <select
                value={filtres.type_acces}
                onChange={(e) => modifierFiltre('type_acces', e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">Tous les types</option>
                {Object.entries(typesAcces).map(([valeur, libelle]) => (
                  <option key={valeur} value={valeur}>{libelle}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs text-slate-500">Du</span>
              <input
                type="date"
                value={filtres.date_debut}
                onChange={(e) => modifierFiltre('date_debut', e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              />
            </label>

            <label className="block">
              <span className="text-xs text-slate-500">Au</span>
              <input
                type="date"
                value={filtres.date_fin}
                onChange={(e) => modifierFiltre('date_fin', e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              />
            </label>
          </div>
        </div>

        {erreur && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{erreur}</span>
          </div>
        )}

        {/* Entrées */}
        {chargement ? (
          <div className="py-12 text-center text-slate-500 text-sm">Chargement…</div>
        ) : entrees.length === 0 ? (
          <div className="py-12 text-center">
            <FileSearch className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              {filtresActifs
                ? 'Aucun accès ne correspond à ces critères.'
                : "Aucun accès enregistré pour l'instant."}
            </p>
            {!filtresActifs && (
              <p className="text-xs text-slate-400 mt-1">
                Le journal se remplit à mesure que les dossiers sont consultés.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-2 font-semibold">Date et heure</th>
                    <th className="py-2 px-2 font-semibold">Utilisateur</th>
                    <th className="py-2 px-2 font-semibold">Accès</th>
                    <th className="py-2 px-2 font-semibold">Bénéficiaire concerné</th>
                  </tr>
                </thead>
                <tbody>
                  {entrees.map((e) => (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                      <td className="py-2.5 px-2 text-slate-500 whitespace-nowrap tabular-nums">
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-300" />
                          {fmtDateHeure(e.date)}
                        </span>
                      </td>
                      <td className="py-2.5 px-2">
                        <div className="font-medium text-slate-800">
                          {e.utilisateur_nom || '—'}
                        </div>
                        <RoleBadge role={e.utilisateur_role} />
                      </td>
                      <td className="py-2.5 px-2 text-slate-700">
                        {e.type_acces_libelle || e.type_acces}
                        {e.details && (
                          <div className="text-xs text-slate-400">{e.details}</div>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-slate-700">
                        {e.apprenant_nom || (e.apprenant_id ? 'Dossier supprimé' : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between gap-3 pt-1">
                <span className="text-xs text-slate-500">
                  Page {page} sur {pages} · {total} entrée{total > 1 ? 's' : ''}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm" className="rounded-xl"
                    disabled={depuis === 0}
                    onClick={() => setDepuis(Math.max(0, depuis - PAR_PAGE))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline" size="sm" className="rounded-xl"
                    disabled={depuis + PAR_PAGE >= total}
                    onClick={() => setDepuis(depuis + PAR_PAGE)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
